/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * SUBMISSION GUARD PREDICATES (v1_668).
 *
 * Two decisions in the documents-first SUBMIT path look similar and were once
 * collapsed onto one flag (submittedRef), which broke completion: the flag the
 * SUBMIT handler sets the instant SUBMIT is accepted — to hard-stop the
 * pre-submit review loop injecting a question in the ~800ms before the deferred
 * finish runs (v1_653) — is NOT the same thing as "the completion has already
 * started". Guarding finishDocumentsFirstChat on that hard-stop flag made it
 * early-return every time and never complete the case: status stuck in_progress,
 * submittedAt never stamped, the input never locked, none of the server-side
 * automation fired, though Alex had already told the client the file was
 * submitted (Sage Capital Management AG, session 65c8a17c, staging 18/09/26).
 *
 * These are pure so the collision cannot silently return: the finish guard and
 * the loop guard read DIFFERENT signals, harnessed.
 */

/**
 * Should finishDocumentsFirstChat run its completion chain?
 *
 * Runs only when the case is not already durably submitted (`submitted` state or
 * a stamped `submittedAt`) and this finish has not already STARTED in this
 * session (`finishStarted`, a dedicated run-once latch). It deliberately does NOT
 * consult the pre-submit-loop hard-stop flag (submittedRef): that flag is set at
 * SUBMIT acceptance, so reading it here would block the very completion this
 * function exists to perform.
 */
export function shouldRunFinish({ submitted, finishStarted, submittedAt } = {}) {
  return !submitted && !finishStarted && !submittedAt;
}

/**
 * Is the pre-submit review loop halted? (v1_653 intent, preserved.)
 *
 * The loop must never start or inject once SUBMIT has been accepted. It halts on
 * the acceptance-time hard-stop flag (`submitStopFlag`, the submittedRef the
 * handler sets synchronously), on the durable `submitted` state, or on a stamped
 * `submittedAt`. Callers add their own transient gates (loading, SOW mode).
 */
export function preSubmitLoopHalted({ submitted, submitStopFlag, submittedAt } = {}) {
  return !!(submitted || submitStopFlag || submittedAt);
}
